import { ApiStatusError, NetworkError, NetworkErrorType } from '../types';
import { showErrorSnackbar, showAuthErrorSnackbar } from '../../components/error';

interface ApiErrorHandlerOptions {
  setError?: (error: string) => void;
  context?: string;
  forceSnackbar?: boolean; // Force show as snackbar instead of error state
}

/**
 * Specialized handler for API errors
 * 
 * @param error - The ApiStatusError to handle
 * @param options - Configuration options
 */
export async function handleApiError(
  error: ApiStatusError,
  options: ApiErrorHandlerOptions = {}
): Promise<void> {
  const { setError, context = 'API Request', forceSnackbar = false } = options;
  
  console.error(`❌ ${context} failed (${error.status}):`, error);

  if (error.isAuthError()) {
    // Authentication errors -> show auth snackbar
    showAuthErrorSnackbar(error.detail || error.message);
  } else if (error.isServerError() && !forceSnackbar) {
    // Server errors -> set error state (unless forced to snackbar)
    if (setError) {
      setError(error.getUserMessage());
    } else {
      showErrorSnackbar(error.detail || error.message, error.traceId, error.instance);
    }
  } else {
    // Client errors or forced snackbar -> show snackbar
    showErrorSnackbar(error.detail || error.message, error.traceId, error.instance);
  }
}

/**
 * Create an ApiStatusError from a Response object
 * 
 * @param response - The HTTP Response object
 * @returns Promise<ApiStatusError>
 */
export async function createApiErrorFromResponse(response: Response): Promise<ApiStatusError> {
  let errorData;
  
  try {
    errorData = await response.json();
  } catch (jsonError) {
    console.error('Failed to parse API error response as JSON:', jsonError);
    // If we can't parse JSON, it might be a network/proxy error
    errorData = {};
  }
  
  // Check for network-related 500 errors (empty response body)
  // This typically happens when the dev server can't reach the backend
  if (response.status === 500 && (!errorData || Object.keys(errorData).length === 0)) {
    console.error('🌐 Network connectivity issue detected (empty 500 response)');
    // Use statically imported NetworkError
    throw new NetworkError(
      'Unable to connect to the server. Please check your internet connection.',
      NetworkErrorType.CONNECTION
    );
  }
  
  // Log the error with appropriate description
  const statusDescriptions: Record<number, string> = {
    400: 'Bad Request',
    401: 'Unauthorized',
    403: 'Forbidden',
    404: 'Not Found',
    422: 'Unprocessable Entity',
    500: 'Internal Server Error'
  };
  
  const description = statusDescriptions[response.status] || 'HTTP Error';
  console.error(`❌ ${description} (${response.status}):`, errorData);
  
  return new ApiStatusError(
    errorData.detail || errorData.message || `HTTP ${response.status}`,
    errorData.status || response.status,
    errorData.traceId,
    errorData.title,
    errorData.instance || response.url || 'Unknown endpoint'
  );
}
